import { useContext, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { ReactiveJsonRoot } from '@ea-lab/reactive-json'
import { builderPlugins } from './builderPlugins'
import { BuilderContext } from './BuilderContextInstance'
import { CONTEXT_EDIT_FLAG } from './editable/contextFields'
import { getPageStateUnitData, setSharedFilterLock } from './editable/unitRegistry'
import { overlayUrlParams } from '../core/urlDataOverride'
import { PARENT_URL_PARAM, visitorPageUrl } from './visitorPageUrl'

/**
 * Query params consumed by the builder's own chrome (`BuilderShell`) — never
 * forwarded to a bloc, so a block declaring `data.layout` (or `host`, …) is not
 * hijacked by the editor's own URL.
 */
const RESERVED_PARAMS = ['edit', 'layout', 'host', 'nid', PARENT_URL_PARAM]

/**
 * Mounts a single bloc's ReactiveJsonRoot with a MEMOIZED `dataOverride`.
 *
 * Memoization is load-bearing for migrated (unit) blocks: ReactiveJsonRoot
 * re-seeds its store whenever `dataOverride` changes by reference
 * (effect dep on `dataOverride`). A unit block's content edits live in that
 * store (written by DataSync), NOT in `bloc.data` — so a fresh `dataOverride`
 * object on every parent re-render (device switch, isDirty toggle, …) would
 * wipe pending edits. Keying the memo on the bloc's actual fields keeps the
 * identity stable until the seed genuinely changes.
 *
 * `apiData` is synthesized here in ONE place from the bloc (the single source):
 * `apiData.data` carries only the writable `objectData`, so a DataSync POST maps
 * cleanly back to field_block_data. `submission_url` is null for an unsaved
 * bloc → the unit renders but stays read-only until the first orchestrated save
 * assigns it (Option A). Non-unit blocks simply ignore `apiData` and read
 * `~~.X` from the merged top-level data.
 *
 * URL query params are overlaid LAST, on the same contract as the page routes
 * (`overlayUrlParams`) : a param wins only over a key the block ALREADY declares
 * in its `data:`. That is what makes a block addressable — `?id=42` on the host
 * page reaches `~~.id`, so a « fiche » block loads its record. It must sit above
 * `bloc.data` because the saved instance holds the field's default (often `''`),
 * which would otherwise mask the URL.
 *
 * Lives in its own module (not `BuilderCanvas`) so `BuilderGridLayout` can mount
 * it without the `BuilderCanvas ↔ BuilderGridLayout` import cycle.
 */
export const BuilderBlockMount = ({ bloc, def, objectContext, alternates, apiPrefix = '' }) => {
  const { search } = useLocation()
  // `useContext` et non `useBuilder()` : un bloc se monte aussi hors provider
  // (aperçus, tests), où le hook lèverait. Absent = rendu ordinaire.
  const builder = useContext(BuilderContext)
  // Un CONTENU édité dans son modèle : les masquages « champ vide » se
  // neutralisent le temps de l'édition (cf. `CONTEXT_EDIT_FLAG`), pour que rien
  // de ce qui reste à renseigner ne disparaisse de l'écran.
  const contextEdit = Boolean(builder?.isEditMode && builder.editScope === 'object')

  const dataOverride = useMemo(() => {
    // Un nouveau `dataOverride` RÉ-AMORCE le store du bloc (ReactiveJsonRoot
    // fait `set('', dataOverride)`). Pour un bloc que le canevas persiste à sa
    // place (aucun `submission_url` : tableau de bord, bloc pas encore créé),
    // `bloc.data` est le seed d'origine : les valeurs écrites depuis DANS le
    // store par un champ de formulaire (filtres d'un widget) seraient perdues au
    // premier re-seed (bascule d'appareil, visibilité, édition d'un autre
    // champ…). Le registre rend cette valeur vivante — elle prime sur le seed.
    const live = getPageStateUnitData(bloc.id)
    const seed = {
      ...(def.rjBuild.data ?? {}),
      ...bloc.data,
      ...(live ?? {}),
    }
    // Only keys the block declares are addressable — `def.rjBuild.data` is the
    // declared surface (`bloc.data` may not carry the key at all yet).
    const declared = def.rjBuild.data ?? {}
    const fromUrl = overlayUrlParams(declared, search, { exclude: RESERVED_PARAMS })
    for (const [key, value] of Object.entries(fromUrl ?? {})) {
      if (value !== declared[key]) seed[key] = value
    }

    // FILTRES PARTAGÉS d'un tableau de bord : les réglages d'un widget vivent
    // dans sa donnée d'INSTANCE (`defaultData` → `apiData.…objectData.data`),
    // que la surcharge d'URL ci-dessus ne touche pas — elle ne vise que la
    // surface déclarée du rjBuild. Un widget ouvre donc explicitement certains
    // de ses réglages au pilotage par l'URL via `sharedFilters:` (liste blanche
    // dans la déf) : le paramètre de même nom, posé par la barre de filtres du
    // tableau de bord, PRIME alors sur la valeur propre du widget, qui redevient
    // le repli dès que le filtre partagé est vidé.
    const unitData = { ...(def.defaultData ?? {}), ...bloc.data, ...(live ?? {}) }
    const shared = def.sharedFilters ?? []
    // Vrai pour chaque clé partagée effectivement pilotée par l'URL en cet
    // instant : l'éditeur s'en sert pour GRISER le réglage local du widget, qui
    // n'aurait aucun effet tant que le filtre du tableau de bord est posé.
    const sharedLocked = {}
    if (shared.length) {
      const declaredShared = Object.fromEntries(
        shared.map((k) => [k, (def.defaultData ?? {})[k]]),
      )
      const sharedFromUrl = overlayUrlParams(declaredShared, search, { exclude: RESERVED_PARAMS })
      // Sur une clé partagée, l'URL fait AUTORITÉ et le repli est la valeur
      // propre du widget — jamais `live`. Sans ce retour à la base, la valeur
      // qu'un filtre partagé a injectée dans le store du widget y RESTE : au
      // vidage du filtre, le paramètre disparaît de l'URL mais chaque widget
      // repartait sur l'ancienne sélection, donc le tableau de bord restait
      // filtré sans plus rien afficher qui l'explique.
      const base = { ...(def.defaultData ?? {}), ...bloc.data }
      // Le verrou se lit sur l'URL, clé par clé : `overlayUrlParams` rend TOUTES
      // les clés déclarées dès qu'UNE a bougé — s'y fier griserait « Pipelines »
      // au seul motif qu'une filiale est filtrée.
      const params = new URLSearchParams(search)
      for (const key of shared) {
        unitData[key] = base[key]
        sharedLocked[key] = params.has(key)
      }
      Object.assign(unitData, sharedFromUrl ?? {})
    }

    return {
      ...seed,
      ...(objectContext ? { _context_: objectContext } : {}),
      // Hors `apiData`, comme `_page_` : un fait d'ÉDITION, jamais de la donnée
      // du bloc — il ne part pas au save. Absent en rendu public, où le
      // masquage d'un champ vide reprend tous ses droits.
      ...(contextEdit ? { [CONTEXT_EDIT_FLAG]: true } : {}),
      // La PAGE elle-même, à la disposition de tout bloc (`~~._page_.url`).
      // Aucun YAML ne sait lire l'adresse courante — il n'y a pas de source
      // déclarative pour `location` —, et c'est pourtant ce qu'un formulaire
      // doit joindre à son envoi pour qu'on sache d'OÙ vient une soumission.
      // Hors `apiData`, comme `_sharedLocked` : c'est un fait de rendu, il ne
      // part pas au save avec `objectData.data`.
      //
      // `url` est la page que le VISITEUR voit : intégrée en iframe chez un
      // client, c'est la page appelante, pas la frame (`visitorPageUrl`) — d'où
      // vient une soumission, ce que partage un bouton « Partager ». `frameUrl`
      // reste l'adresse propre de la page Orbit ; `path` et `host` aussi (ce
      // sont eux qui routent et marquent l'entrée de menu active).
      _page_: {
        url: visitorPageUrl(),
        frameUrl: typeof window === 'undefined' ? '' : window.location.href,
        path: typeof window === 'undefined' ? '' : window.location.pathname,
        host: typeof window === 'undefined' ? '' : window.location.host,
        // La page dans ses autres langues (`{en: '/en/agencies'}`), donnée par
        // `resolve` : un sélecteur de langue mène à CETTE page traduite.
        alternates: alternates ?? {},
        // Préfixe d'API de la langue servie (`/en`) : une unité partagée montée
        // par `UnitMount` se charge dans la langue de la page.
        apiPrefix,
      },
      // L'UNITÉ elle-même, par son identifiant de nœud. Un bloc formulaire le
      // joint à son envoi (`unitId`) : c'est la seule chose qui permette au
      // serveur de retrouver LES DESTINATAIRES réglés sur ce formulaire-là.
      // Les adresses ne voyagent jamais dans la requête — un endpoint public
      // qui accepterait un `to:` du client serait un relais de courrier
      // ouvert. Hors `apiData`, comme `_page_` : ce n'est pas de la donnée du
      // bloc, ça ne part pas au save. `null` tant que le bloc n'est pas
      // enregistré (rien à notifier : il n'est encore sur aucune page servie).
      _unit_: { nid: bloc.nid ?? null },
      // Hors `apiData` : c'est un fait d'AFFICHAGE (le filtre du tableau de bord
      // est-il posé ?), pas de la donnée du widget — il ne doit pas partir au
      // save avec `objectData.data`.
      _sharedLocked: sharedLocked,
      apiData: {
        item_url: bloc.syncUrls?.item_url ?? null,
        submission_url: bloc.syncUrls?.submission_url ?? null,
        delete_url: bloc.syncUrls?.delete_url ?? null,
        // Seed the unit's content from the def's defaultData, with the instance's
        // own data taking precedence. This restores the render-time seed the
        // legacy model got from `rjBuild.data` (e.g. a footer's link lists that
        // were only ever in the seed, never persisted per-instance): a unit must
        // render its full default content even when the stored instance only
        // holds the fields the editor actually touched. Shallow merge — the
        // instance wins wholesale per key, so an explicitly-cleared list stays
        // cleared and edited values are never overwritten by a default.
        data: { objectData: { builderId: bloc.id, data: unitData, hiddenOn: bloc.hiddenOn } },
      },
    }
  }, [def, bloc.id, bloc.nid, bloc.data, bloc.hiddenOn, bloc.syncUrls, objectContext, contextEdit, search, alternates, apiPrefix])

  // Publie les clés que l'URL pilote pour CE bloc, à l'usage du save : elles ne
  // doivent pas être récoltées depuis le store (cf. `harvestData`), sinon le
  // filtre partagé se figerait en réglage propre du widget. Retiré au démontage
  // pour ne pas laisser de verrou fantôme derrière un bloc supprimé.
  const lockedKeys = Object.keys(dataOverride._sharedLocked ?? {})
    .filter((k) => dataOverride._sharedLocked[k])
  const lockedSig = lockedKeys.join(',')
  useEffect(() => {
    setSharedFilterLock(bloc.id, lockedSig ? lockedSig.split(',') : [])
    return () => setSharedFilterLock(bloc.id, [])
  }, [bloc.id, lockedSig])

  return (
    <ReactiveJsonRoot
      maybeRawAppRjBuild={def.rjBuild}
      dataOverride={dataOverride}
      plugins={builderPlugins}
    />
  )
}
